if (!customElements.get('container-mobile-order')) {
  customElements.define('container-mobile-order', class extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;

      const sectionId = this.dataset.sectionId;
      this.section = document.getElementById(`MainProduct-${sectionId}`);
      this.summary = document.getElementById(`ContainerSummary-${sectionId}`);
      this.summaryHeading = this.summary?.querySelector('.container-summary-heading h3');
      this.primaryButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
      this.form = this.primaryButton?.form;
      if (!this.section || !this.summary || !this.form) return;

      this.button = this.querySelector('button');
      this.selectionLink = this.querySelector('.container-mobile-order__selection');
      this.pricing = this.querySelector('.container-mobile-order__pricing');
      this.price = this.querySelector('[data-order-price]');
      this.exclusivePrice = this.querySelector('[data-order-price-exclusive]');
      this.selection = this.querySelector('[data-order-selection]');
      this.detail = this.querySelector('[data-order-detail]');
      this.error = this.querySelector('[data-order-error]');
      this.sync = this.sync.bind(this);
      this.submit = this.submit.bind(this);
      this.resume = this.resume.bind(this);
      this.scheduleVisibilityCheck = this.scheduleVisibilityCheck.bind(this);

      this.button.addEventListener('click', this.submit);
      this.selectionLink.addEventListener('click', this.resume);
      this.section.addEventListener('change', this.sync);
      this.form.addEventListener('submit', this.sync);
      this.observer = new MutationObserver(this.sync);
      this.observer.observe(this.summary, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden'],
      });

      // Check once per frame while scrolling, including after layout changes.
      window.addEventListener('scroll', this.scheduleVisibilityCheck, { passive: true });
      window.addEventListener('resize', this.scheduleVisibilityCheck);
      this.resizeObserver = new ResizeObserver(() => this.updateVisibility());
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.summary);
      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      window.removeEventListener('scroll', this.scheduleVisibilityCheck);
      window.removeEventListener('resize', this.scheduleVisibilityCheck);
      cancelAnimationFrame(this.visibilityFrame);
      this.button?.removeEventListener('click', this.submit);
      this.selectionLink?.removeEventListener('click', this.resume);
      this.section?.removeEventListener('change', this.sync);
      this.form?.removeEventListener('submit', this.sync);
      this.observer = null;
      document.documentElement.style.removeProperty('--container-order-bar-height');
    }

    sync() {
      if (!this.isConnected || !this.primaryButton) return;

      const title = this.summary.querySelector('.product__selected-variant--title')?.textContent.trim();
      const size = this.summary.querySelector('.product__selected-variant--size')?.textContent.trim();
      const frequency = this.summary.querySelector('.product__selected-variant--frequency')?.textContent.trim();
      const duration = this.summary.querySelector('.product__selected-variant--duration')?.textContent.trim();
      const price = this.summary.querySelector('.total-price')?.textContent.trim();
      const exclusivePrice = this.summary.querySelector('[data-price-exclusive]')?.textContent.trim();
      const busy = this.primaryButton.classList.contains('loading');
      const changingWaste = this.section.hasAttribute('aria-busy');
      const nextStep = this.section.nextStep;
      if (!Number.isInteger(nextStep)) return;
      const incomplete = nextStep < this.section.summaryStep || changingWaste;
      this.pricing.hidden = incomplete;
      this.selection.textContent = incomplete ? this.section.selectionHint : [title, size].filter(Boolean).join(' — ');
      this.detail.textContent = [frequency, duration].filter(Boolean).join(' · ');
      this.detail.hidden = incomplete || !this.detail.textContent;
      if (price) this.price.textContent = price;
      if (exclusivePrice && this.exclusivePrice) this.exclusivePrice.textContent = exclusivePrice;
      this.button.disabled = incomplete || busy || this.primaryButton.disabled || this.primaryButton.getAttribute('aria-disabled') === 'true';
      this.button.setAttribute('aria-busy', String(busy));

      const errorWrapper = this.summary.querySelector('.product-form__error-message-wrapper');
      this.error.textContent = errorWrapper && !errorWrapper.hidden
        ? errorWrapper.querySelector('.product-form__error-message')?.textContent.trim() || ''
        : '';
      this.error.hidden = !this.error.textContent;
      this.updateVisibility();
    }

    resume(event) {
      if (!Number.isInteger(this.section.nextStep) || this.section.hasAttribute('aria-busy')) return;
      event.preventDefault();
      this.section.goToStep(this.section.nextStep);
    }

    submit() {
      if (this.button.disabled || this.section.nextStep < this.section.summaryStep || this.section.hasAttribute('aria-busy')) return;
      // The original button handles availability, cart restrictions, and loading.
      if (this.primaryButton.disabled || this.primaryButton.getAttribute('aria-disabled') === 'true') return;
      this.primaryButton.click();
      this.sync();
    }

    scheduleVisibilityCheck() {
      if (this.visibilityFrame) return;
      this.visibilityFrame = requestAnimationFrame(() => {
        this.visibilityFrame = null;
        this.updateVisibility();
      });
    }

    updateVisibility() {
      const heading = (this.summaryHeading || this.summary).getBoundingClientRect();
      const titleReachedMiddle = heading.top + heading.height / 2 <= window.innerHeight / 2;
      const visibleTop = parseFloat(getComputedStyle(this.summary).scrollMarginTop) || 0;
      const summaryPassed = this.summary.getBoundingClientRect().bottom <= visibleTop;
      this.hidden = !Number.isInteger(this.section.nextStep) || (titleReachedMiddle && !summaryPassed);
      this.updateHeight();
    }

    updateHeight() {
      const height = Math.ceil(this.getBoundingClientRect().height);
      if (height === this.lastHeight) return;
      this.lastHeight = height;
      document.documentElement.style.setProperty('--container-order-bar-height', `${height}px`);
    }
  });
}
